import React, { useEffect, useState } from 'react';
import { Stack, Select, MenuItem, FormControl, useTheme, useMediaQuery } from '@mui/material';
import { useTranslation } from 'gatsby-plugin-react-i18next';

import { PassengersByTicketType, TicketType } from '../../../../domain/tickets';
import { PassengerTicketCounter } from './passenger-ticket-counter';

const TICKET_PASSENGER_I18N: Record<keyof PassengersByTicketType, string> = {
  adult: 'tickets_service.passenger_types.individual',
  [TicketType.UnaccompaniedMinor]: 'tickets_service.passenger_types.minor_unaccompanied',
  [TicketType.AsistedPass]: 'tickets_service.passenger_types.assisted_traveler',
};

const passengerCategories = [
  {
    id: 'adult',
    i18n_label: TICKET_PASSENGER_I18N.adult,
    i18n_age_range_label: 'tickets_service.age_ranges.adult',
  },
  {
    id: TicketType.UnaccompaniedMinor,
    i18n_label: TICKET_PASSENGER_I18N[TicketType.UnaccompaniedMinor],
    i18n_age_range_label: 'tickets_service.age_ranges.from_13_to_17_years',
  },
  {
    id: TicketType.AsistedPass,
    i18n_label: TICKET_PASSENGER_I18N[TicketType.AsistedPass],
    i18n_age_range_label: 'tickets_service.age_ranges.over_18_years',
  },
] as const;

const createCategoryLabel = (count, label) => {
  return count > 0 ? `${count} ${label}` : '';
};

interface Props {
  passengerCounts: PassengersByTicketType;
  onChange: (passengers: PassengersByTicketType) => void;
}

export default function PassengerTicketManager({ passengerCounts, onChange }: Props) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [categorySelector, setCategorySelector] = useState(t('tickets_service.inputs.number_of_passengers'));

  const updatePassengerCount = (category: keyof PassengersByTicketType, change: number) => {
    const updatedCount = passengerCounts[category] + change;
    if (updatedCount >= 0) {
      onChange({ ...passengerCounts, [category]: updatedCount });
    }
  };

  useEffect(() => {
    if (
      passengerCounts.adult ||
      passengerCounts[TicketType.UnaccompaniedMinor] ||
      passengerCounts[TicketType.AsistedPass]
    ) {
      const labels = [
        createCategoryLabel(passengerCounts.adult, t(TICKET_PASSENGER_I18N.adult)),
        createCategoryLabel(
          passengerCounts[TicketType.UnaccompaniedMinor],
          t(TICKET_PASSENGER_I18N[TicketType.UnaccompaniedMinor]),
        ),
        createCategoryLabel(passengerCounts[TicketType.AsistedPass], t(TICKET_PASSENGER_I18N[TicketType.AsistedPass])),
      ];

      setCategorySelector(labels.filter(Boolean).join(', '));
    } else {
      setCategorySelector(t('tickets_service.inputs.number_of_passengers'));
    }
  }, [passengerCounts, t]);

  const renderPassengerCounters = () => (
    <Stack direction={isMobile ? 'column' : 'row'} gap="24px">
      {passengerCategories.map(({ id, i18n_label, i18n_age_range_label }) => (
        <PassengerTicketCounter
          key={id.toString()}
          title={t(i18n_label)}
          count={passengerCounts[id]}
          ageRange={t(i18n_age_range_label)}
          onIncrement={() => updatePassengerCount(id, 1)}
          onDecrement={() => updatePassengerCount(id, -1)}
        />
      ))}
    </Stack>
  );

  const mobileSx = { width: '100%' };

  const desktopSx = { display: 'flex', flex: 1 };

  return (
    <FormControl sx={isMobile ? mobileSx : desktopSx}>
      <Select
        id="category-selector"
        value={categorySelector}
        labelId="category-selector-label"
        renderValue={(value) => (value === 'default' ? t('tickets_service.inputs.number_of_passengers') : value)}
        sx={{ color: '#6C6D70' }}
        MenuProps={{
          anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
          transformOrigin: { vertical: 'top', horizontal: 'left' },
        }}
        onChange={(event) => setCategorySelector(event.target.value)}
      >
        <MenuItem value={categorySelector} style={{ backgroundColor: 'transparent' }} disableRipple disableTouchRipple>
          {renderPassengerCounters()}
        </MenuItem>
      </Select>
    </FormControl>
  );
}
